import React, { useEffect } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import {
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerSubheader,
  DrawerTitle,
  StyledDrawer,
} from './side-peek-drawer.styled';

const DEFAULT_WIDTH = '680px';

interface SidePeekDrawerProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  subheader?: React.ReactNode;
  footer?: React.ReactNode;
  width?: string;
  contentBackground?: 'white' | 'grey';
  variant?: 'temporary' | 'persistent';
  flat?: boolean;
}

const SidePeekDrawer: React.FC<SidePeekDrawerProps> = ({
  open,
  title,
  onClose,
  children,
  subheader,
  footer,
  width = DEFAULT_WIDTH,
  contentBackground = 'white',
  variant = 'temporary',
  flat = false,
}) => {
  useEffect(() => {
    if (variant !== 'persistent' || !open) return undefined;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [variant, open, onClose]);

  return (
    <StyledDrawer
      anchor="right"
      variant={variant}
      open={open}
      onClose={onClose}
      $width={width}
      $flat={flat}
    >
      <DrawerHeader>
        <DrawerTitle variant="h5">{title}</DrawerTitle>
        <DrawerCloseButton
          onClick={onClose}
          aria-label="Close"
          data-testid="side-peek-drawer-close"
        >
          <CloseRoundedIcon fontSize="inherit" />
        </DrawerCloseButton>
      </DrawerHeader>
      {subheader && <DrawerSubheader>{subheader}</DrawerSubheader>}
      <DrawerContent
        $grey={contentBackground === 'grey'}
        $hideScrollbar={variant === 'persistent'}
      >
        {children}
      </DrawerContent>
      {footer && <DrawerFooter>{footer}</DrawerFooter>}
    </StyledDrawer>
  );
};

export default SidePeekDrawer;
